import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { paymentApi } from '@formosa/shared/api/payment';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  filterAddressesByCategory,
  listUnboundChannels,
  maskWalletAddress,
  resolveBoundAddressLabel,
  resolveChannelLabel,
} from '../../constants/withdrawConstants';
import { useTranslation } from '../../i18n/LocaleContext';

export default function WithdrawAddressesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const categoryKey = location.state?.categoryKey;
  const categoryLabel = location.state?.categoryLabel;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [channels, setChannels] = useState([]);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [successBanner] = useState(Boolean(location.state?.bound));

  const scopedAddresses = useMemo(
    () => (categoryKey ? filterAddressesByCategory(addresses, channels) : addresses),
    [addresses, channels, categoryKey]
  );

  const unboundChannels = useMemo(
    () => listUnboundChannels(channels, addresses),
    [channels, addresses]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const channelParams = categoryKey ? { categoryKey } : undefined;
      const [addressList, channelList] = await Promise.all([
        paymentApi.listWithdrawAddresses(),
        paymentApi.listWithdrawChannelOptions(channelParams),
      ]);
      setAddresses(Array.isArray(addressList) ? addressList : []);
      setChannels(Array.isArray(channelList) ? channelList : []);
    } catch (err) {
      setError(err.message || t('withdraw.loadFailed'));
      setAddresses([]);
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, [categoryKey, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function handleAddChannel(channel) {
    navigate('/withdraw/addresses/bind', {
      state: {
        channel,
        from: location.state?.from || '/withdraw/addresses',
        categoryKey,
        categoryLabel,
      },
    });
  }

  function handleManage(address) {
    setMenuOpenId(null);
    navigate('/withdraw/addresses/unbind', {
      state: {
        address,
        from: '/withdraw/addresses',
        categoryKey,
        categoryLabel,
      },
    });
  }

  function handleBack() {
    if (categoryKey) {
      navigate('/withdraw/methods', { state: { from: location.state?.from || '/withdraw' } });
      return;
    }
    navigate(location.state?.from || '/withdraw');
  }

  return (
    <div className="deposit-page withdraw-addresses-page">
      {successBanner && (
        <div className="withdraw-addresses-page__banner">{t('withdraw.bindSuccessBanner')}</div>
      )}

      <header className="deposit-page__header">
        <button
          type="button"
          className="deposit-page__back"
          aria-label={t('common.back')}
          onClick={handleBack}
        >
          ‹
        </button>
        <h1>{categoryLabel || t('withdraw.addressListTitle')}</h1>
        <span className="deposit-page__header-spacer" />
      </header>

      {loading && <p className="deposit-page__status">{t('common.loading')}</p>}
      {error && <p className="deposit-page__error">{error}</p>}

      {!loading && !error && (
        <>
          {scopedAddresses.length > 0 && (
            <section className="withdraw-addresses-section">
              <h2 className="withdraw-addresses-section__title">{t('withdraw.boundSection')}</h2>
              <ul className="withdraw-address-list">
                {scopedAddresses.map((item) => (
                  <li key={item.id} className="withdraw-address-card">
                    <div className="withdraw-address-card__main">
                      <strong>{resolveBoundAddressLabel(item, channels)}</strong>
                      <span className="withdraw-address-card__addr">
                        {maskWalletAddress(item.walletAddress)}
                      </span>
                    </div>
                    <div className="withdraw-address-card__menu-wrap">
                      <button
                        type="button"
                        className="withdraw-address-card__menu-btn"
                        aria-label={t('withdraw.manageAddress')}
                        onClick={() => setMenuOpenId(menuOpenId === item.id ? null : item.id)}
                      >
                        ⋯
                      </button>
                      {menuOpenId === item.id && (
                        <div className="withdraw-address-card__menu">
                          <button type="button" onClick={() => handleManage(item)}>
                            {t('withdraw.unbindAction')}
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="withdraw-addresses-section">
            <h2 className="withdraw-addresses-section__title">{t('withdraw.addSection')}</h2>
            <ul className="deposit-currency-list">
              {unboundChannels.length === 0 ? (
                <li className="deposit-page__empty">{t('withdraw.noChannelToAdd')}</li>
              ) : (
                unboundChannels.map((channel) => (
                  <li key={channel.id || channel.code}>
                    <button
                      type="button"
                      className="deposit-currency-item"
                      onClick={() => handleAddChannel(channel)}
                    >
                      <span className="deposit-currency-item__icon" aria-hidden>
                        {channel.image ? (
                          <img src={resolveMemberMediaUrl(channel.image)} alt="" />
                        ) : (
                          <span>₮</span>
                        )}
                      </span>
                      <span className="deposit-currency-item__label">
                        {resolveChannelLabel(channel)}
                      </span>
                      <span className="deposit-currency-item__add" aria-hidden>
                        +
                      </span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
